import React, { useEffect, useRef, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { parseISO } from "date-fns";
import { addLocale } from "primereact/api";
import { Col, ModalBody, Row } from "react-bootstrap";

import { axiosPostRequest, axiosPutRequest } from "../../api/ApiClient";
import DialogMessage from "../../components/DialogMessage";
import { ptBrDatepickerTranslations } from "../../i18n/locales/pt-br";
import BloqueioPlanoAtividadeDTO from "../../model/dto/BloqueioPlanoAtividadeDTO";
import IModalProps from "../../model/ModalProps";
import ButtonStyles from "../../styles/components/ButtonStyles";
import DialogStyles from "../../styles/components/DialogStyles";
import InputCalendarStyles from "../../styles/components/InputCalendarStyles";
import ModalStyles from "../../styles/components/ModalStyles";
import { MASK_DATE } from "../../utils/constants";
import GenericUtils from "../../utils/GenericUtils";
import StringUtils from "../../utils/types/StringUtils";

addLocale("pt-BR", ptBrDatepickerTranslations);

interface IModalLockPeriodProps extends IModalProps {
    params: any;
}

function ModalLockPeriod(props: IModalLockPeriodProps) {
    const { instance } = useMsal();
    const acessToken = GenericUtils.getAccessTokenDocente();

    const translate = {
        lockPeriod: StringUtils.translate("lockPeriod"),
        operationFailure: StringUtils.translate("operationFailure"),
        lockPeriodSuccses: StringUtils.translate("lockPeriodSuccses"),
        setPeriod: StringUtils.translate("setPeriod"),
    };

    const [period, setPeriod] = useState<any>(null);
    const [errorMessage, setErrorMessage] = useState("");
    const [successMessage, setSuccessMessage] = useState("");

    const periodRef = useRef<any>();
    const isFormValid = period && Boolean(period[0]) && Boolean(period[1]);

    async function createLockPeriod(dto: BloqueioPlanoAtividadeDTO) {
        return axiosPostRequest(
            "/docente/v1/bloqueios-plano-atividade",
            dto,
            acessToken,
            instance
        );
    }

    async function updateLockPeriod(dto: BloqueioPlanoAtividadeDTO) {
        return axiosPutRequest(
            `/docente/v1/bloqueios-plano-atividade/${String(
                dto.planoAtividadeBloqueioId
            )}`,
            dto,
            acessToken,
            instance
        );
    }

    const handleSubmit = async () => {
        if (isFormValid) {
            const dto: BloqueioPlanoAtividadeDTO = {
                planoAtividadeBloqueioId: props.params?.id || null,
                planoAtividadeBloqueioDtInicio: StringUtils.formatDateString(
                    period[0]?.toISOString(),
                    MASK_DATE
                ),
                planoAtividadeBloqueioDtFim: StringUtils.formatDateString(
                    period[1]?.toISOString(),
                    MASK_DATE
                ),
            };

            const response = props.params?.id
                ? await updateLockPeriod(dto)
                : await createLockPeriod(dto);

            if (GenericUtils.isSuccessStatus(response.status)) {
                setSuccessMessage(translate.lockPeriodSuccses);
            } else {
                setErrorMessage(
                    response.data?.details || translate.operationFailure
                );
            }
        }
    };

    useEffect(() => {
        if (props.open) {
            if (props.params) {
                setPeriod([
                    parseISO(props.params.dtInicio),
                    parseISO(props.params.dtFim),
                ]);
            } else {
                setPeriod(null);
            }
        }
    }, [props.open]);

    useEffect(() => {
        if (
            periodRef?.current &&
            period &&
            Boolean(period[0]) &&
            Boolean(period[1])
        ) {
            periodRef?.current?.hide();
        }
    }, [period]);

    const handleCloseDialogSuccess = () => {
        setSuccessMessage("");
        if (props.onClose) props.onClose({ success: true });
    };

    const DialogSuccess = (
        <DialogMessage
            type="success"
            show={Boolean(successMessage)}
            title={successMessage}
            onClose={handleCloseDialogSuccess}
        />
    );

    const handleCloseDialogError = () => {
        setErrorMessage("");
    };

    const DialogError = (
        <DialogMessage
            type="error"
            show={Boolean(errorMessage)}
            title={errorMessage}
            onClose={handleCloseDialogError}
        />
    );

    return (
        <DialogStyles.StyledModal
            show={props.open}
            onHide={props.onClose}
            size="sm"
            centered
            backdrop="static"
            keyboard={false}
            aria-labelledby="contained-modal-title-vcenter"
        >
            <DialogStyles.StyledModalHeader closeButton />
            <ModalBody>
                <ModalStyles.StyledModalTitle>
                    {translate.lockPeriod}
                </ModalStyles.StyledModalTitle>
                <Row className="mt-2">
                    <Col>
                        <InputCalendarStyles.StyledCalendar
                            ref={periodRef}
                            placeholder={translate.setPeriod}
                            dateFormat="dd/mm/yy"
                            locale="pt-BR"
                            selectionMode="range"
                            showButtonBar
                            minDate={new Date()}
                            readOnlyInput
                            autoZIndex
                            touchUI
                            value={period}
                            onChange={(e: any) => setPeriod(e.target?.value)}
                        />
                    </Col>
                </Row>
            </ModalBody>
            <DialogStyles.StyledModalFooter>
                <ButtonStyles.BtnPrimary
                    disabled={!isFormValid}
                    onClick={() => handleSubmit()}
                    className="mx-auto"
                >
                    Ok
                </ButtonStyles.BtnPrimary>
            </DialogStyles.StyledModalFooter>
            {successMessage ? DialogSuccess : null}
            {errorMessage ? DialogError : null}
        </DialogStyles.StyledModal>
    );
}

export default ModalLockPeriod;
